import React, { useState, useEffect, useRef } from 'react';
import { Menu, X, Code, Smartphone, Database, Mail, Phone, MapPin, Linkedin, Github, Twitter, ChevronRight, Award, Users, Zap, ArrowRight, CheckCircle, Star, Sparkles } from 'lucide-react';
import gafLogo from '../src/assets/gaf.png';

const Gafinity = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [scrollY, setScrollY] = useState(0);
  const [activeSection, setActiveSection] = useState('home');
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });
  const [visibleElements, setVisibleElements] = useState(new Set());
  const [dynamicTextIndex, setDynamicTextIndex] = useState(0);

  const dynamicTexts = [
    "the Future of Technology",
    "Automate Your Business",
    "Scale Up Your Business"
  ];

  useEffect(() => {
    const timer = setTimeout(() => setIsLoading(false), 2500);
    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    const interval = setInterval(() => {
      setDynamicTextIndex((prev) => (prev + 1) % dynamicTexts.length);
    }, 4000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    const handleScroll = () => {
      setScrollY(window.scrollY);
      
      const sections = ['home', 'services', 'projects', 'about', 'contact'];
      const current = sections.find(section => {
        const element = document.getElementById(section);
        if (element) {
          const rect = element.getBoundingClientRect();
          return rect.top <= 150 && rect.bottom >= 150;
        }
        return false;
      });
      if (current) setActiveSection(current);

      // Intersection observer for scroll animations
      const elements = document.querySelectorAll('.scroll-reveal');
      elements.forEach(el => {
        const rect = el.getBoundingClientRect();
        if (rect.top < window.innerHeight * 0.85) {
          setVisibleElements(prev => new Set([...prev, el.id]));
        }
      });
    };

    const handleMouseMove = (e) => {
      setMousePosition({ x: e.clientX, y: e.clientY });
    };

    window.addEventListener('scroll', handleScroll);
    window.addEventListener('mousemove', handleMouseMove);
    handleScroll();
    
    return () => {
      window.removeEventListener('scroll', handleScroll);
      window.removeEventListener('mousemove', handleMouseMove);
    };
  }, []);

  const scrollToSection = (id) => {
    const element = document.getElementById(id);
    if (element) {
      const offset = 80;
      const elementPosition = element.getBoundingClientRect().top;
      const offsetPosition = elementPosition + window.pageYOffset - offset;
      
      window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth'
      });
      setIsMenuOpen(false);
    }
  };

  if (isLoading) {
    return (
      <div className="fixed inset-0 bg-gradient-to-br from-slate-950 via-purple-950 to-slate-950 flex items-center justify-center z-50">
        <div className="text-center">
          <div className="relative w-24 h-24 mx-auto mb-6">
            <div className="absolute inset-0 border-4 border-purple-500/30 border-t-purple-500 rounded-full animate-spin"></div>
            <div className="absolute inset-3 border-4 border-blue-500/30 border-t-blue-500 rounded-full animate-spin" style={{animationDirection: 'reverse', animationDuration: '0.8s'}}></div>
            <div className="absolute inset-6 border-4 border-pink-500/30 border-t-pink-500 rounded-full animate-spin" style={{animationDuration: '1.2s'}}></div>
          </div>
          <div className="space-y-3">
            <h2 className="text-4xl font-bold bg-gradient-to-r from-blue-400 via-purple-400 to-pink-400 bg-clip-text text-transparent">
              Gafinity
            </h2>
            <div className="flex items-center justify-center space-x-2">
              <div className="w-2 h-2 bg-purple-500 rounded-full animate-pulse"></div>
              <div className="w-2 h-2 bg-blue-500 rounded-full animate-pulse" style={{animationDelay: '0.2s'}}></div>
              <div className="w-2 h-2 bg-pink-500 rounded-full animate-pulse" style={{animationDelay: '0.4s'}}></div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const services = [
    {
      icon: <Code className="w-10 h-10 sm:w-12 sm:h-12" />,
      title: "Web Development",
      description: "Build responsive, scalable web applications with modern frameworks and best practices for optimal performance.",
      features: ["React & Next.js", "E-Commerce Solutions", "Progressive Web Apps"]
    },
    {
      icon: <Smartphone className="w-10 h-10 sm:w-12 sm:h-12" />,
      title: "Mobile App Development",
      description: "Create native and cross-platform mobile experiences that engage users and drive business growth.",
      features: ["iOS & Android", "React Native", "UI/UX Design"]
    },
    {
      icon: <Database className="w-10 h-10 sm:w-12 sm:h-12" />,
      title: "ERP Systems",
      description: "Streamline business operations with custom enterprise solutions designed for efficiency and scale.",
      features: ["Custom Modules", "Cloud Integration", "Real-time Analytics"]
    }
  ];

  const projects = [
    { 
      title: "E-Commerce Platform", 
      category: "Web Development", 
      tech: "React, Node.js", 
      gradient: "from-blue-600 via-blue-500 to-cyan-500",
      bgImage: "https://images.unsplash.com/photo-1557821552-17105176677c?w=800&q=80"
    },
    { 
      title: "Healthcare System", 
      category: "Web Development", 
      tech: "Angular, NodeJs, Firebase", 
      gradient: "from-purple-600 via-purple-500 to-pink-500",
      bgImage: "https://images.unsplash.com/photo-1576091160399-112ba8d25d1d?w=800&q=80"
    },
    { 
      title: "Student LMS AI Based System", 
      category: "Web Development", 
      tech: "Angular, NodeJs, MongoDB", 
      gradient: "from-cyan-600 via-teal-500 to-green-500",
      bgImage: "https://images.unsplash.com/photo-1501504905252-473c47e087f8?w=800&q=80"
    }
  ];

  const stats = [
    // { icon: <Award className="w-6 h-6 sm:w-8 sm:h-8" />, value: "50+", label: "Projects Completed" },
    // { icon: <Users className="w-6 h-6 sm:w-8 sm:h-8" />, value: "30+", label: "Happy Clients" },
    { icon: <Zap className="w-6 h-6 sm:w-8 sm:h-8" />, value: "3+", label: "Years Experience" },
    { icon: <Star className="w-6 h-6 sm:w-8 sm:h-8" />, value: "100%", label: "Client Satisfaction" }
  ];

  const achievements = [
    "ISO 9001 Certified",
    "Award-Winning Team",
    "24/7 Support",
    "Agile Methodology"
  ];

  return (
    <div className="min-h-screen bg-slate-950 text-white overflow-x-hidden">
      {/* Animated Background */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none z-0">
        <div 
          className="absolute w-96 h-96 bg-purple-600/20 rounded-full blur-3xl transition-all duration-1000 ease-out"
          style={{
            left: `${mousePosition.x - 192}px`,
            top: `${mousePosition.y - 192}px`,
          }}
        ></div>
        <div className="absolute top-1/4 -left-48 w-96 h-96 bg-blue-600/10 rounded-full blur-3xl animate-pulse"></div>
        <div className="absolute bottom-1/4 -right-48 w-96 h-96 bg-purple-600/10 rounded-full blur-3xl animate-pulse" style={{animationDelay: '1s'}}></div>
      </div>

      {/* Navbar */}
      <nav className={`fixed w-full z-50 transition-all duration-500 ${scrollY > 20 ? 'bg-slate-950/80 backdrop-blur-xl shadow-lg shadow-purple-500/10 border-b border-slate-800/50' : 'bg-transparent'}`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16 sm:h-20">
            <div className="flex items-center space-x-2 sm:space-x-3 cursor-pointer group" onClick={() => scrollToSection('home')}>
              <div className="relative w-10 h-10 sm:w-12 sm:h-12 group-hover:scale-110 transition-transform duration-500">
                <img 
                  src={gafLogo} 
                  alt="Gafinity Logo" 
                  className="w-full h-full object-contain"
                />
              </div>
              <span style={{ fontFamily: "'Satisfy', cursive", height: '5rem', marginTop: '40px' }} className="satisfy-text text-xl sm:text-2xl lg:text-3xl font-bold bg-gradient-to-r from-blue-400 via-purple-400 to-pink-400 bg-clip-text text-transparent">
                Gafinity
              </span>
            </div>

            <div className="hidden lg:flex items-center space-x-1 xl:space-x-2">
              {['home', 'services', 'projects', 'about', 'contact'].map((item) => (
                <button
                  key={item}
                  onClick={() => scrollToSection(item)}
                  className={`relative px-4 xl:px-6 py-2 capitalize text-sm xl:text-base transition-all duration-300 group ${
                    activeSection === item ? 'text-purple-400' : 'text-gray-300 hover:text-white'
                  }`}
                >
                  {item}
                  <span className={`absolute bottom-0 left-0 w-full h-0.5 bg-gradient-to-r from-blue-500 to-purple-500 transform origin-left transition-transform duration-300 ${
                    activeSection === item ? 'scale-x-100' : 'scale-x-0 group-hover:scale-x-100'
                  }`}></span>
                </button>
              ))}
              <button
                onClick={() => scrollToSection('contact')}
                className="ml-4 px-4 xl:px-6 py-2 bg-gradient-to-r from-blue-600 to-purple-600 rounded-full text-sm xl:text-base font-semibold hover:shadow-lg hover:shadow-purple-500/50 transition-all duration-300 transform hover:scale-105"
              >
                Get Started
              </button>
            </div>

            <button 
              className="lg:hidden p-2 hover:bg-slate-800/50 rounded-lg transition-colors"
              onClick={() => setIsMenuOpen(!isMenuOpen)}
            >
              {isMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        <div className={`lg:hidden transition-all duration-500 ease-in-out ${isMenuOpen ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0'} overflow-hidden`}>
          <div className="bg-slate-900/95 backdrop-blur-xl border-t border-slate-800/50">
            <div className="px-4 py-3 space-y-1">
              {['home', 'services', 'projects', 'about', 'contact'].map((item) => (
                <button
                  key={item}
                  onClick={() => scrollToSection(item)}
                  className={`block w-full text-left px-4 py-3 capitalize rounded-lg transition-all duration-300 ${
                    activeSection === item 
                      ? 'bg-gradient-to-r from-blue-600/20 to-purple-600/20 text-purple-400' 
                      : 'hover:bg-slate-800/50 text-gray-300'
                  }`}
                >
                  {item}
                </button>
              ))}
            </div>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section id="home" className="relative min-h-screen flex items-center justify-center pt-20 sm:pt-24 px-4 sm:px-6 lg:px-8">
        <div className="absolute inset-0 bg-gradient-to-br from-slate-950 via-purple-950/50 to-slate-950"></div>
        
        <div className="relative z-10 max-w-7xl mx-auto w-full">
          <div className="text-center space-y-6 sm:space-y-8">
            <div className="inline-flex items-center px-3 sm:px-4 py-1.5 sm:py-2 bg-slate-800/50 backdrop-blur-sm rounded-full border border-purple-500/30 mb-4 sm:mb-6 overflow-hidden">
              <Sparkles className="w-3 h-3 sm:w-4 sm:h-4 text-purple-400 mr-2 flex-shrink-0" />
              <span className="text-xs sm:text-sm whitespace-nowrap">
                <span className="text-gray-300">Introducing you to </span>
                <span 
                  key={dynamicTextIndex}
                  className="bg-gradient-to-r from-blue-400 via-purple-400 to-pink-400 bg-clip-text text-transparent font-semibold inline-block animate-fade-slide"
                >
                  {dynamicTexts[dynamicTextIndex]}
                </span>
              </span>
            </div>

            <h1 className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl xl:text-8xl font-bold leading-tight px-4">
              <span className="block mb-2 sm:mb-4 bg-gradient-to-r from-white to-gray-400 bg-clip-text text-transparent" style={{height: '120px'}}>
                Empowering Digital
              </span>
              <span className="block bg-gradient-to-r from-blue-400 via-purple-400 to-pink-400 bg-clip-text text-transparent animate-gradient">
                Growth & Innovation
              </span>
            </h1>

            <p className="text-base sm:text-lg md:text-xl lg:text-2xl text-gray-400 max-w-4xl mx-auto leading-relaxed px-4">
              Transform your vision into reality with cutting-edge technology solutions designed for scale, performance, and user delight
            </p>

            <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mt-8 sm:mt-12 px-4">
              <button
                onClick={() => scrollToSection('contact')}
                className="group w-full sm:w-auto px-6 sm:px-8 py-3 sm:py-4 bg-gradient-to-r from-blue-600 to-purple-600 rounded-full font-semibold text-sm sm:text-base lg:text-lg hover:shadow-2xl hover:shadow-purple-500/50 transition-all duration-300 transform hover:scale-105 flex items-center justify-center space-x-2"
              >
                <span>Start Your Project</span>
                <ArrowRight className="w-4 h-4 sm:w-5 sm:h-5 group-hover:translate-x-1 transition-transform" />
              </button>
              <button
                onClick={() => scrollToSection('projects')}
                className="w-full sm:w-auto px-6 sm:px-8 py-3 sm:py-4 bg-slate-800/50 backdrop-blur-sm border border-slate-700 rounded-full font-semibold text-sm sm:text-base lg:text-lg hover:bg-slate-800 hover:border-purple-500/50 transition-all duration-300"
              >
                View Our Work
              </button>
            </div>

            <div className="grid grid-cols-2 lg:grid-cols-2 gap-4 sm:gap-6 lg:gap-8 mt-12 sm:mt-16 lg:mt-20 max-w-5xl mx-auto px-4">
              {stats.map((stat, idx) => (
                <div 
                  key={idx} 
                  className="group relative bg-slate-900/50 backdrop-blur-sm rounded-2xl p-4 sm:p-6 border border-slate-800 hover:border-purple-500/50 transition-all duration-500 transform hover:scale-105 hover:-translate-y-2"
                >
                  <div className="absolute inset-0 bg-gradient-to-br from-blue-600/5 to-purple-600/5 rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
                  <div className="relative z-10 text-center">
                    <div className="flex justify-center mb-2 sm:mb-3 text-purple-400 group-hover:scale-110 transition-transform duration-300">
                      {stat.icon}
                    </div>
                    <div className="text-2xl sm:text-3xl lg:text-4xl font-bold bg-gradient-to-r from-blue-400 to-purple-400 bg-clip-text text-transparent mb-1 sm:mb-2">
                      {stat.value}
                    </div>
                    <div className="text-xs sm:text-sm text-gray-400">{stat.label}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className="absolute bottom-8 sm:bottom-12 left-1/2 transform -translate-x-1/2 animate-bounce">
          <div className="w-6 h-10 border-2 border-gray-600 rounded-full p-1">
            <div className="w-1.5 h-3 bg-gradient-to-b from-purple-400 to-transparent rounded-full mx-auto animate-pulse"></div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section id="services" className="relative py-16 sm:py-20 lg:py-32 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12 sm:mb-16 lg:mb-20 scroll-reveal" id="services-header">
            <div className="inline-flex items-center px-3 sm:px-4 py-1.5 sm:py-2 bg-purple-500/10 backdrop-blur-sm rounded-full border border-purple-500/30 mb-4 sm:mb-6">
              <span className="text-xs sm:text-sm text-purple-400 font-semibold">OUR EXPERTISE</span>
            </div>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl xl:text-6xl font-bold mb-4 sm:mb-6">
              <span className="bg-gradient-to-r from-white to-gray-400 bg-clip-text text-transparent">
                Services That Drive
              </span>
              <br />
              <span className="bg-gradient-to-r from-blue-400 to-purple-400 bg-clip-text text-transparent">
                Digital Success
              </span>
            </h2>
            <p className="text-base sm:text-lg lg:text-xl text-gray-400 max-w-3xl mx-auto">
              Comprehensive technology solutions tailored to your business needs
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-8">
            {services.map((service, idx) => (
              <div
                key={idx}
                className="group relative bg-gradient-to-br from-slate-900 to-slate-950 rounded-3xl p-6 sm:p-8 hover:shadow-2xl hover:shadow-purple-500/20 transition-all duration-700 transform hover:-translate-y-3 border border-slate-800 hover:border-purple-500/50 scroll-reveal"
                id={`service-${idx}`}
                style={{animationDelay: `${idx * 0.15}s`}}
              >
                <div className="absolute inset-0 bg-gradient-to-br from-blue-600/5 via-purple-600/5 to-pink-600/5 rounded-3xl opacity-0 group-hover:opacity-100 transition-opacity duration-700"></div>
                
                <div className="relative z-10">
                  <div className="w-16 h-16 sm:w-20 sm:h-20 bg-gradient-to-br from-blue-600 to-purple-600 rounded-2xl flex items-center justify-center mb-4 sm:mb-6 text-white transform group-hover:scale-110 group-hover:rotate-6 transition-all duration-500 shadow-lg shadow-purple-500/30">
                    {service.icon}
                  </div>
                  
                  <h3 className="text-xl sm:text-2xl font-bold mb-3 sm:mb-4 group-hover:text-transparent group-hover:bg-gradient-to-r group-hover:from-blue-400 group-hover:to-purple-400 group-hover:bg-clip-text transition-all duration-300">
                    {service.title}
                  </h3>
                  
                  <p className="text-sm sm:text-base text-gray-400 leading-relaxed mb-4 sm:mb-6">
                    {service.description}
                  </p>

                  {/* <div className="space-y-2 sm:space-y-3">
                    {service.features.map((feature, fIdx) => (
                      <div key={fIdx} className="flex items-center space-x-2 sm:space-x-3 text-xs sm:text-sm text-gray-500 group-hover:text-gray-400 transition-colors">
                        <CheckCircle className="w-4 h-4 text-purple-500 flex-shrink-0" />
                        <span>{feature}</span>
                      </div>
                    ))}
                  </div> */}

                  {/* <button className="mt-6 sm:mt-8 flex items-center space-x-2 text-purple-400 group-hover:text-purple-300 transition-colors text-sm sm:text-base">
                    <span className="font-semibold">Learn More</span>
                    <ChevronRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                  </button> */}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Projects Section */}
      <section id="projects" className="relative py-16 sm:py-20 lg:py-32 px-4 sm:px-6 lg:px-8 bg-slate-900/30">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12 sm:mb-16 lg:mb-20 scroll-reveal" id="projects-header">
            <div className="inline-flex items-center px-3 sm:px-4 py-1.5 sm:py-2 bg-blue-500/10 backdrop-blur-sm rounded-full border border-blue-500/30 mb-4 sm:mb-6">
              <span className="text-xs sm:text-sm text-blue-400 font-semibold">PORTFOLIO</span>
            </div>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl xl:text-6xl font-bold mb-4 sm:mb-6">
              <span className="bg-gradient-to-r from-white to-gray-400 bg-clip-text text-transparent">
                Projects That Inspire
              </span>
              <br />
              <span className="bg-gradient-to-r from-blue-400 to-purple-400 bg-clip-text text-transparent">
                & Deliver Results
              </span>
            </h2>
            <p className="text-base sm:text-lg lg:text-xl text-gray-400 max-w-3xl mx-auto">
              Showcasing excellence across diverse industries
            </p>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6 lg:gap-8">
            {projects.map((project, idx) => (
              <div
                key={idx}
                className="group relative h-64 sm:h-72 lg:h-80 rounded-3xl overflow-hidden cursor-pointer transform hover:scale-105 transition-all duration-700 scroll-reveal"
                id={`project-${idx}`}
              >
                {/* Background Image */}
                <div 
                  className="absolute inset-0 bg-cover bg-center transition-transform duration-700 group-hover:scale-110"
                  style={{backgroundImage: `url(${project.bgImage})`}}
                ></div>
                
                {/* Gradient Overlay */}
                <div className={`absolute inset-0 bg-gradient-to-br ${project.gradient} opacity-60 group-hover:opacity-70 transition-opacity duration-500`}></div>
                
                {/* Dark Gradient Overlay */}
                <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/40 to-transparent group-hover:from-black/80 transition-all duration-500"></div>
                
                <div className="absolute inset-0 p-6 sm:p-8 flex flex-col justify-end">
                  <div className="transform translate-y-4 group-hover:translate-y-0 transition-transform duration-500">
                    <div className="inline-block px-3 py-1 bg-white/20 backdrop-blur-sm rounded-full text-xs sm:text-sm mb-3 sm:mb-4 border border-white/30">
                      {project.category}
                    </div>
                    <h3 className="text-xl sm:text-2xl font-bold mb-2">{project.title}</h3>
                    <p className="text-xs sm:text-sm text-gray-300 mb-3 sm:mb-4 opacity-0 group-hover:opacity-100 transition-opacity duration-500">{project.tech}</p>
                    <div className="flex items-center space-x-2 text-white opacity-0 group-hover:opacity-100 transition-all duration-500">
                      <span className="text-xs sm:text-sm font-semibold">View Project</span>
                      <ArrowRight className="w-4 h-4 transform group-hover:translate-x-2 transition-transform duration-300" />
                    </div>
                  </div>
                </div>

                <div className="absolute top-4 right-4 w-12 h-12 bg-white/10 backdrop-blur-sm rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transform scale-50 group-hover:scale-100 transition-all duration-500">
                  <ChevronRight className="w-6 h-6" />
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* About Section */}
      <section id="about" className="relative py-16 sm:py-20 lg:py-32 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
            <div className="scroll-reveal" id="about-content">
              <div className="inline-flex items-center px-3 sm:px-4 py-1.5 sm:py-2 bg-purple-500/10 backdrop-blur-sm rounded-full border border-purple-500/30 mb-6 sm:mb-8">
                <span className="text-xs sm:text-sm text-purple-400 font-semibold">ABOUT US</span>
              </div>
              
              <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold mb-6 sm:mb-8">
                <span className="bg-gradient-to-r from-white to-gray-400 bg-clip-text text-transparent">
                  Building Tomorrow's
                </span>
                <br />
                <span className="bg-gradient-to-r from-blue-400 to-purple-400 bg-clip-text text-transparent">
                  Digital Solutions Today
                </span>
              </h2>
              
              <div className="space-y-4 sm:space-y-6 text-sm sm:text-base lg:text-lg text-gray-400 leading-relaxed">
                <p>
                  At Gafinity, we're more than just a technology company — we're your partners in digital transformation. With a passion for innovation and a commitment to excellence, we've helped businesses across industries achieve their technological ambitions.
                </p>
                <p>
                  Our team of expert developers, designers, and strategists work collaboratively to deliver solutions that are not only technically robust but also user-centric and business-focused. We believe in creating lasting relationships built on trust, transparency, and exceptional results.
                </p>
                <div className="pt-4 sm:pt-6">
                  <p className="text-lg sm:text-xl font-semibold bg-gradient-to-r from-blue-400 to-purple-400 bg-clip-text text-transparent">
                    Your vision. Our expertise. Unlimited possibilities.
                  </p>
                </div>
              </div>

              {/* <div className="grid grid-cols-2 gap-3 sm:gap-4 mt-8 sm:mt-12">
                {achievements.map((achievement, idx) => (
                  <div key={idx} className="flex items-center space-x-2 sm:space-x-3 p-3 sm:p-4 bg-slate-900/50 backdrop-blur-sm rounded-xl border border-slate-800 hover:border-purple-500/50 transition-all duration-300">
                    <CheckCircle className="w-4 h-4 sm:w-5 sm:h-5 text-purple-400 flex-shrink-0" />
                    <span className="text-xs sm:text-sm font-semibold">{achievement}</span>
                  </div>
                ))}
              </div> */}
            </div>

            <div className="relative scroll-reveal" id="about-visual">
              <div className="relative">
                <div className="absolute inset-0 bg-gradient-to-br from-blue-600 to-purple-600 rounded-3xl blur-2xl opacity-20"></div>
                <div className="relative bg-gradient-to-br from-slate-900 to-slate-950 rounded-3xl p-1">
                  <div className="bg-slate-950 rounded-3xl p-6 sm:p-8 lg:p-12">
                    <div className="space-y-6 sm:space-y-8">
                      <div className="group flex items-start space-x-4 sm:space-x-6 p-4 sm:p-6 bg-slate-900/50 rounded-2xl hover:bg-slate-900 transition-all duration-300 border border-slate-800 hover:border-purple-500/50">
                        <div className="w-12 h-12 sm:w-16 sm:h-16 bg-gradient-to-br from-blue-500 to-purple-600 rounded-xl sm:rounded-2xl flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform duration-300">
                          <Zap className="w-6 h-6 sm:w-8 sm:h-8" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <h4 className="font-bold text-base sm:text-lg lg:text-xl mb-1 sm:mb-2">Innovation First</h4>
                          <p className="text-xs sm:text-sm text-gray-400">Leveraging cutting-edge technologies to solve complex challenges</p>
                        </div>
                      </div>

                      <div className="group flex items-start space-x-4 sm:space-x-6 p-4 sm:p-6 bg-slate-900/50 rounded-2xl hover:bg-slate-900 transition-all duration-300 border border-slate-800 hover:border-purple-500/50">
                        <div className="w-12 h-12 sm:w-16 sm:h-16 bg-gradient-to-br from-purple-500 to-pink-600 rounded-xl sm:rounded-2xl flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform duration-300">
                          <Users className="w-6 h-6 sm:w-8 sm:h-8" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <h4 className="font-bold text-base sm:text-lg lg:text-xl mb-1 sm:mb-2">Client-Centric Approach</h4>
                          <p className="text-xs sm:text-sm text-gray-400">Your success is our mission, every step of the way</p>
                        </div>
                      </div>

                      <div className="group flex items-start space-x-4 sm:space-x-6 p-4 sm:p-6 bg-slate-900/50 rounded-2xl hover:bg-slate-900 transition-all duration-300 border border-slate-800 hover:border-purple-500/50">
                        <div className="w-12 h-12 sm:w-16 sm:h-16 bg-gradient-to-br from-cyan-500 to-blue-600 rounded-xl sm:rounded-2xl flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform duration-300">
                          <Award className="w-6 h-6 sm:w-8 sm:h-8" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <h4 className="font-bold text-base sm:text-lg lg:text-xl mb-1 sm:mb-2">Quality Assurance</h4>
                          <p className="text-xs sm:text-sm text-gray-400">Delivering excellence through rigorous testing and optimization</p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="relative py-16 sm:py-20 lg:py-32 px-4 sm:px-6 lg:px-8 bg-slate-900/30">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12 sm:mb-16 lg:mb-20 scroll-reveal" id="contact-header">
            <div className="inline-flex items-center px-3 sm:px-4 py-1.5 sm:py-2 bg-purple-500/10 backdrop-blur-sm rounded-full border border-purple-500/30 mb-4 sm:mb-6">
              <span className="text-xs sm:text-sm text-purple-400 font-semibold">GET IN TOUCH</span>
            </div>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl xl:text-6xl font-bold mb-4 sm:mb-6">
              <span className="bg-gradient-to-r from-white to-gray-400 bg-clip-text text-transparent">
                Let's Build Something
              </span>
              <br />
              <span className="bg-gradient-to-r from-blue-400 to-purple-400 bg-clip-text text-transparent">
                Amazing Together
              </span>
            </h2>
            <p className="text-base sm:text-lg lg:text-xl text-gray-400 max-w-3xl mx-auto">
              Ready to transform your business? We're here to help
            </p>
          </div>

          <div className="grid lg:grid-cols-2 gap-8 sm:gap-12 lg:gap-16">
            <div className="scroll-reveal" id="contact-form">
              <div className="bg-gradient-to-br from-slate-900 to-slate-950 rounded-3xl p-6 sm:p-8 lg:p-10 border border-slate-800">
                <div className="space-y-6">
                  <div>
                    <label className="block text-sm font-semibold mb-2 text-gray-300">Full Name</label>
                    <input
                      type="text"
                      placeholder="John Doe"
                      className="w-full px-4 py-3 sm:py-4 bg-slate-950 border border-slate-700 rounded-xl focus:border-purple-500 focus:outline-none focus:ring-2 focus:ring-purple-500/20 transition-all duration-300 text-sm sm:text-base"
                      onChange={(e) => {}}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold mb-2 text-gray-300">Email Address</label>
                    <input
                      type="email"
                      placeholder="john@example.com"
                      className="w-full px-4 py-3 sm:py-4 bg-slate-950 border border-slate-700 rounded-xl focus:border-purple-500 focus:outline-none focus:ring-2 focus:ring-purple-500/20 transition-all duration-300 text-sm sm:text-base"
                      onChange={(e) => {}}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold mb-2 text-gray-300">Phone Number</label>
                    <input
                      type="tel"
                      placeholder="+91 000 000 0000"
                      className="w-full px-4 py-3 sm:py-4 bg-slate-950 border border-slate-700 rounded-xl focus:border-purple-500 focus:outline-none focus:ring-2 focus:ring-purple-500/20 transition-all duration-300 text-sm sm:text-base"
                      onChange={(e) => {}}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold mb-2 text-gray-300">Project Details</label>
                    <textarea
                      rows="5"
                      placeholder="Tell us about your project..."
                      className="w-full px-4 py-3 sm:py-4 bg-slate-950 border border-slate-700 rounded-xl focus:border-purple-500 focus:outline-none focus:ring-2 focus:ring-purple-500/20 transition-all duration-300 resize-none text-sm sm:text-base"
                      onChange={(e) => {}}
                    ></textarea>
                  </div>
                  <button
                    onClick={() => alert('Thank you for reaching out! We will get back to you within 24 hours.')}
                    className="group w-full px-6 py-3 sm:py-4 bg-gradient-to-r from-blue-600 to-purple-600 rounded-xl font-semibold hover:shadow-2xl hover:shadow-purple-500/50 transition-all duration-300 transform hover:scale-105 flex items-center justify-center space-x-2 text-sm sm:text-base"
                  >
                    <span>Send Message</span>
                    <ArrowRight className="w-4 h-4 sm:w-5 sm:h-5 group-hover:translate-x-1 transition-transform" />
                  </button>
                </div>
              </div>
            </div>

            <div className="space-y-6 sm:space-y-8 scroll-reveal" id="contact-info">
              <div className="group bg-gradient-to-br from-slate-900 to-slate-950 rounded-3xl p-6 sm:p-8 border border-slate-800 hover:border-purple-500/50 transition-all duration-500 transform hover:-translate-y-2">
                <div className="flex items-start space-x-4 sm:space-x-6">
                  <div className="w-12 h-12 sm:w-16 sm:h-16 bg-gradient-to-br from-blue-600 to-purple-600 rounded-2xl flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform duration-300">
                    <Mail className="w-5 h-5 sm:w-6 sm:h-6" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <h4 className="font-bold text-base sm:text-lg mb-1 sm:mb-2">Email Us</h4>
                    <p className="text-sm sm:text-base text-gray-400 break-all">developers.gafinity@gmail.com</p>
                  </div>
                </div>
              </div>

              <div className="group bg-gradient-to-br from-slate-900 to-slate-950 rounded-3xl p-6 sm:p-8 border border-slate-800 hover:border-purple-500/50 transition-all duration-500 transform hover:-translate-y-2">
                <div className="flex items-start space-x-4 sm:space-x-6">
                  <div className="w-12 h-12 sm:w-16 sm:h-16 bg-gradient-to-br from-purple-600 to-pink-600 rounded-2xl flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform duration-300">
                    <Phone className="w-5 h-5 sm:w-6 sm:h-6" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <h4 className="font-bold text-base sm:text-lg mb-1 sm:mb-2">Call Us</h4>
                    <p className="text-sm sm:text-base text-gray-400">+91 99406 94611</p>
                    <p className="text-sm sm:text-base text-gray-400">Mon-Fri: 9AM - 6PM IST</p>
                  </div>
                </div>
              </div>

              {/* <div className="group bg-gradient-to-br from-slate-900 to-slate-950 rounded-3xl p-6 sm:p-8 border border-slate-800 hover:border-purple-500/50 transition-all duration-500 transform hover:-translate-y-2">
                <div className="flex items-start space-x-4 sm:space-x-6">
                  <div className="w-12 h-12 sm:w-16 sm:h-16 bg-gradient-to-br from-cyan-600 to-blue-600 rounded-2xl flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform duration-300">
                    <MapPin className="w-5 h-5 sm:w-6 sm:h-6" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <h4 className="font-bold text-base sm:text-lg mb-1 sm:mb-2">Visit Us</h4>
                    <p className="text-sm sm:text-base text-gray-400">123 Tech Street</p>
                    <p className="text-sm sm:text-base text-gray-400">Silicon Valley, CA 94025</p>
                  </div>
                </div>
              </div> */}

              {/* <div className="rounded-3xl overflow-hidden border border-slate-800 h-48 sm:h-64">
                <iframe
                  title="Location Map"
                  src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3168.6394861269794!2d-122.08424908469223!3d37.42199997982525!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x808fba02425dad8f%3A0x6c296c66619367e0!2sGoogleplex!5e0!3m2!1sen!2sus!4v1635180420000!5m2!1sen!2sus"
                  className="w-full h-full"
                  loading="lazy"
                ></iframe>
              </div> */}
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="relative bg-slate-950 py-8 sm:py-12 px-4 sm:px-6 lg:px-8 border-t border-slate-800">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col md:flex-row justify-between items-center space-y-6 md:space-y-0">
            <div className="flex items-center space-x-3">
              {/* <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-purple-600 rounded-xl flex items-center justify-center"> */}
                <img 
                  src={gafLogo} 
                  alt="Gafinity Logo" 
                  style = {{ height: '40px', width: '40px' }}
                  className="w-full h-full object-contain"
                />
              {/* </div> */}
              <div>
                <div style={{ fontFamily: "'Satisfy', cursive", }} className="satisfy-text text-xl font-bold bg-gradient-to-r from-blue-400 to-purple-400 bg-clip-text text-transparent">
                  Gafinity
                </div>
                <div className="text-xs text-gray-500">Empowering Digital Growth</div>
              </div>
            </div>

            <div className="text-center md:text-left">
              <p className="text-sm text-gray-400">
                © 2025 Gafinity Technologies. All rights reserved.
              </p>
              <p className="text-xs text-gray-500 mt-1">
                Crafted with innovation and precision
              </p>
            </div>

            {/* <div className="flex items-center space-x-4 sm:space-x-6">
              <a 
                href="#" 
                className="group w-10 h-10 bg-slate-900 hover:bg-gradient-to-br hover:from-blue-600 hover:to-purple-600 rounded-xl flex items-center justify-center transition-all duration-300 transform hover:scale-110 hover:-translate-y-1"
              >
                <Linkedin className="w-5 h-5 text-gray-400 group-hover:text-white transition-colors" />
              </a>
              <a 
                href="#" 
                className="group w-10 h-10 bg-slate-900 hover:bg-gradient-to-br hover:from-blue-600 hover:to-purple-600 rounded-xl flex items-center justify-center transition-all duration-300 transform hover:scale-110 hover:-translate-y-1"
              >
                <Github className="w-5 h-5 text-gray-400 group-hover:text-white transition-colors" />
              </a>
              <a 
                href="#" 
                className="group w-10 h-10 bg-slate-900 hover:bg-gradient-to-br hover:from-blue-600 hover:to-purple-600 rounded-xl flex items-center justify-center transition-all duration-300 transform hover:scale-110 hover:-translate-y-1"
              >
                <Twitter className="w-5 h-5 text-gray-400 group-hover:text-white transition-colors" />
              </a>
            </div> */}
          </div>
        </div>
      </footer>

      <style>{`
        @keyframes gradient {
          0%, 100% { background-position: 0% 50%; }
          50% { background-position: 100% 50%; }
        }
        
        .animate-gradient {
          background-size: 200% 200%;
          animation: gradient 3s ease infinite;
        }

        @keyframes fade-slide {
          0% {
            opacity: 0;
            transform: translateY(-10px);
          }
          10% {
            opacity: 1;
            transform: translateY(0);
          }
          90% {
            opacity: 1;
            transform: translateY(0);
          }
          100% {
            opacity: 0;
            transform: translateY(10px);
          }
        }

        .animate-fade-slide {
          animation: fade-slide 2s ease-in-out;
        }

        .scroll-reveal {
          opacity: ${visibleElements.size > 0 ? 1 : 0};
          transform: translateY(${visibleElements.size > 0 ? '0' : '30px'});
          transition: all 0.8s cubic-bezier(0.4, 0, 0.2, 1);
        }

        @media (max-width: 640px) {
          .scroll-reveal {
            transform: translateY(${visibleElements.size > 0 ? '0' : '20px'});
          }
        }

        /* Smooth zoom responsiveness */
        @media (min-width: 1024px) and (max-width: 1280px) {
          html {
            font-size: 15px;
          }
        }

        @media (min-width: 1280px) and (max-width: 1536px) {
          html {
            font-size: 16px;
          }
        }

        @media (min-width: 1536px) {
          html {
            font-size: 18px;
          }
        }

        /* Prevent horizontal scroll */
        body {
          overflow-x: hidden;
        }
      `}</style>
    </div>
  );
};

export default Gafinity;